import React, { Suspense, useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { Button, Input } from 'antd';
import ReactPlayer from 'react-player';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-duotone-svg-icons';
import { notificationsAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import Loader from '../../shared-components/loader/Loader';
import { Spinner } from '../../shared-components/Spinner';

function LearningCenter() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [showVideos, setShowVideos] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(-1);
  const [searchTerm, setSearchTerm] = useState('');

  function getDepartments() {
    setLoading(true);
    let url = 'learning/videos/departments';
    if (selectedLevel !== -1) {
      url += `?level=${selectedLevel}`;
    }
    api.get(url)
      .then((response) => {
        if (response.data.success) {
          setDepartments(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function getCategories() {
    if (selectedDepartment === null) {
      return;
    }
    setLoading(true);
    let url = `learning/videos/categories?department=${selectedDepartment.id}`;
    if (selectedLevel !== -1) {
      url += `&level=${selectedLevel}`;
    }
    api.get(url)
      .then((response) => {
        if (response.data.success) {
          setCategories(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function getVideos() {
    setLoading(true);
    let url = `learning/videos?department=${selectedDepartment ? selectedDepartment?.id : ''}&category=${selectedCategory ? selectedCategory?.id : ''}&searchTerm=${searchTerm}`;
    if (selectedLevel !== -1) {
      url += `&level=${selectedLevel}`;
    }
    api.get(url)
      .then((response) => {
        if (response.data.success) {
          setVideos(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (selectedDepartment) {
      getCategories();
    } else {
      setCategories([]);
    }
  }, [selectedDepartment]);

  useEffect(() => {
    if (selectedCategory) {
      getVideos();
    } else {
      setVideos([]);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (showVideos) {
      getVideos();
    } else {
      setVideos([]);
    }
  }, [showVideos]);

  useEffect(() => {
    setSelectedDepartment(null);
    setSelectedCategory(null);
    getDepartments()
  }, []);

  return (
    <MainPage requiresLearning>
      <div className="flex mt-4 space-y-2 space-x-2">
        <H1 text={t('learning_center')} className="mr-auto" />
      </div>
      <Input.Search
        className="mb-4 mt-2"
        placeholder={`${t('search_videos')}...`}
        allowClear
        enterButton={t('search')}
        value={searchTerm}
        onChange={(obj) => setSearchTerm(obj.target.value)}
        onClear={() => {
          setSearchTerm('');
          setShowVideos(false);
        }}
        onSearch={(val) => {
          setSelectedDepartment(null);
          setSelectedCategory(null);
          if (!showVideos) {
            setShowVideos(true)
          } else {
            getVideos();
          }
        }}
      />
      {(!showVideos && !selectedDepartment) && (
        <div className="mt-4 grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {loading && (
            <div className="flex">
              <Spinner className="mx-auto mt-44 text-accentColor" big />
            </div>
          )}
          {!loading && _.map(departments, (department) => (
            <button
              className="col-span-1 relative h-32 cursor-pointer border-2 border-gray-300 rounded-md py-4 px-2 w-full overflow-hidden"
              type="button"
              onClick={() => setSelectedDepartment(department)}
            >
              {department?.imageUrl && (
                <div
                  className="bg-cover bg-center absolute top-0 left-0 right-0 bottom-0 opacity-40"
                  style={{ backgroundImage: `url(${department?.imageUrl})` }}
                />
              )}
              <div className="absolute top-0 left-0 right-0 bottom-0">
                <div className="flex flex-col h-full p-2">
                  <div className="my-auto">
                    <p className="text-2xl font-bold">
                      {i18n.language === 'en' ? department.nameEng : department.nameSpa}
                    </p>
                    <p className="text-left truncate">
                      {i18n.language === 'en' ? department.descriptionEng : department.descriptionSpa}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {(!showVideos && !selectedCategory && selectedDepartment) && (
        <div>
          <button
            type="button"
            className="flex cursor-pointer space-x-2"
            onClick={() => setSelectedDepartment(null)}
          >
            <FontAwesomeIcon className="text-xl" icon={faArrowLeft} />
            <strong className="text-lg -mt-1">{i18n.language === 'en' ? selectedDepartment?.nameEng : selectedDepartment?.nameSpa}</strong>
          </button>
          {loading && (
            <div className="flex">
              <Spinner className="mx-auto mt-44 text-accentColor" big />
            </div>
          )}
          <p className="text-center my-2 italic">{i18n.language === 'en' ? selectedDepartment.descriptionEng : selectedDepartment.descriptionSpa}</p>
          <div className="mt-4 grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {!loading && _.map(categories, (category) => (
              <button
                className="col-span-1 relative h-32 cursor-pointer border-2 border-gray-300 rounded-md py-4 px-2 w-full overflow-hidden"
                type="button"
                onClick={() => setSelectedCategory(category)}
              >
                {category?.imageUrl && (
                  <div
                    className="bg-cover bg-center absolute top-0 left-0 right-0 bottom-0 opacity-40"
                    style={{ backgroundImage: `url(${category?.imageUrl})` }}
                  />
                )}
                <div className="absolute top-0 left-0 right-0 bottom-0">
                  <div className="flex flex-col h-full p-2">
                    <div className="my-auto">
                      <p className="text-2xl font-bold">
                        {i18n.language === 'en' ? category.nameEng : category.nameSpa}
                      </p>
                      <p className="text-left truncate">
                        {i18n.language === 'en' ? category.descriptionEng : category.descriptionSpa}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {((showVideos || selectedCategory)) && (
        <div>
          <button
            type="button"
            className="flex cursor-pointer space-x-2"
            onClick={() => {
              setShowVideos(false);
              setSelectedCategory(null);
              setSearchTerm('');
            }}
          >
            <FontAwesomeIcon className="text-xl" icon={faArrowLeft} />
            <strong className="text-lg -mt-1">{selectedCategory ? (i18n.language === 'en' ? `${selectedDepartment?.nameEng} / ${selectedCategory?.nameEng}` : `${selectedDepartment?.nameSpa} / ${selectedCategory?.nameSpa}`) : t('go_back')}</strong>
          </button>
          {selectedCategory && (
            <p className="text-center my-2 italic">{i18n.language === 'en' ? selectedCategory.descriptionEng : selectedCategory.descriptionSpa}</p>
          )}
          {loading && (
            <div className="flex">
              <Spinner className="mx-auto mt-44 text-accentColor" big />
            </div>
          )}
          <div className="mt-4 grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {!loading && _.map(videos, (video) => (
              <button
                className="col-span-1 relative h-32 cursor-pointer border-2 border-gray-300 rounded-md py-4 px-2 w-full overflow-hidden"
                type="button"
                onClick={() => history.push(`/learning/video/${video.id}`)}
              >
                <div
                  className="bg-cover bg-center absolute top-0 left-0 right-0 bottom-0 opacity-40"
                  style={{ backgroundImage: `url(${i18n.language === 'en' ? video.previewImageUrlEng : video.previewImageUrlSpa})` }}
                />
                <div className="absolute top-0 left-0 right-0 bottom-0">
                  <div className="flex flex-col h-full p-2">
                    <div className="my-auto">
                      <p className="text-2xl font-bold">
                        {i18n.language === 'en' ? video.nameEng : video.nameSpa}
                      </p>
                      <p className="text-left truncate">
                        {i18n.language === 'en' ? video.shortDescriptionEng : video.shortDescriptionSpa}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </MainPage>
  );
}

export default LearningCenter;
